<template>
  <div class="product-image-manager" :class="{ 'is-disabled': disabled }">
    <div class="image-grid">
      <div v-for="image in images" :key="image.clientImageId || image.id" :class="['image-card', { 'is-main': image.isMain }]">
        <button class="remove-img" type="button" :disabled="disabled" @click="removeImage(image)">×</button>
        <span v-if="image.isMain" class="main-badge">主图</span>
        <div class="image-preview-box">
          <img :src="preview(image)" alt="">
          <div class="image-hover-actions">
            <button type="button" @click.stop="openPreview(image)">预览</button>
            <template v-if="!image.isMain">
              <i></i>
              <button type="button" :disabled="disabled" @click.stop="setMain(image)">设为主图</button>
            </template>
          </div>
        </div>
        <div class="version-toggle">
          <button
            :class="{ 'is-active': image.selectedImageType === 'processed' }"
            :disabled="disabled || isImageProcessing(image)"
            type="button"
            @click="selectVersion(image, 'processed')"
          >{{ isImageProcessing(image) ? '生成中' : 'AI优化' }}</button>
          <button :class="{ 'is-active': image.selectedImageType === 'original' }" :disabled="disabled" type="button" @click="selectVersion(image, 'original')">原图</button>
        </div>
        <p v-if="image.processStatus === 'failed'" class="process-tip">{{ image.processMessage || 'AI优化失败，已保留原图' }}</p>
      </div>

      <el-upload
        v-if="images.length < 5 && !disabled"
        class="image-add-upload"
        action=""
        accept="image/jpeg,image/jpg,image/png"
        :show-file-list="false"
        :http-request="customUpload"
        :disabled="uploadLoading || processLoading"
      >
        <div class="add-card" v-loading="uploadLoading || processLoading">+</div>
      </el-upload>
    </div>
    <p class="image-requirement">{{ images.length }}/5 张图片 · 单张图片≤10M，建议尺寸：1000×1000以内的正方形，支持 jpeg / jpg / png 格式</p>

    <el-dialog
      class="product-image-preview-dialog"
      :visible.sync="previewDialog.visible"
      append-to-body
      width="760px"
      :show-close="true"
    >
      <div class="large-preview-wrap">
        <img v-if="previewDialog.url" :src="previewDialog.url" alt="">
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { refinePart } from '@/utils/api/agent-hub-product-case';
import { uploadSupplierProductImage } from '@/utils/api/product-case';
import { AI_OPERATION, IMAGE_TYPE, PRODUCT_IMAGE_UPLOAD_SOURCE } from '@/utils/product-case/constants';
import {
  createImageItem,
  extractRspData,
  getImagePreview,
  normalizeImageItem,
  normalizePart,
  preferProcessedImages,
  selectImageVersion,
  setPartMainImage,
  sortPartImages,
  syncMainFlag
} from '@/utils/product-case/model';
import { createMockUpload, processMockImages } from '@/utils/product-case/mock';

export default {
  name: 'ProductImageManager',
  props: {
    part: {
      type: Object,
      required: true
    },
    siteType: {
      type: Number,
      default: 1
    },
    mockEnabled: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      uploadLoading: false,
      processLoading: false,
      processingImageIds: [],
      previewDialog: {
        visible: false,
        url: ''
      }
    };
  },
  computed: {
    images() {
      return sortPartImages(this.part.productPictureList || [], this.part.defaultPhoto);
    }
  },
  methods: {
    preview(image) {
      return getImagePreview(image);
    },
    openPreview(image) {
      this.previewDialog.url = getImagePreview(image);
      this.previewDialog.visible = true;
    },
    getImageKey(image) {
      return image.clientImageId || image.id || image.originalHashCode;
    },
    isImageProcessing(image) {
      return this.processingImageIds.includes(this.getImageKey(image));
    },
    validateFile(file) {
      if (this.images.length >= 5) {
        this.$message.warning('单个零件最多上传 5 张图片');
        return false;
      }
      const isImage = /^image\/(jpeg|jpg|png)$/.test(file.type);
      if (!isImage) {
        this.$message.warning('仅支持 jpeg / jpg / png 格式图片');
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        this.$message.warning('单张图片不能超过 10M');
        return false;
      }
      return true;
    },
    async customUpload({ file }) {
      if (this.disabled || this.uploadLoading || this.processLoading) return;
      if (!this.validateFile(file)) return;
      this.uploadLoading = true;
      let image;
      try {
        let uploadData;
        if (this.mockEnabled) {
          uploadData = createMockUpload(file);
        } else {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('source', PRODUCT_IMAGE_UPLOAD_SOURCE);
          const uploadRes = await uploadSupplierProductImage(formData);
          uploadData = extractRspData(uploadRes);
        }
        image = createImageItem({ ...uploadData, originalName: uploadData.originalName || file.name }, this.images.length + 1);
        image.hashCode = uploadData.hashCode;
        image.previewUrl = uploadData.previewUrl || image.previewUrl;
        this.part.productPictureList.push(image);
        if (!this.part.defaultPhoto) this.part.defaultPhoto = image.selectedHashCode;
        syncMainFlag(this.part);
        this.$emit('change', this.part);
      } catch (error) {
        const message = (error && ((error.rspResult && error.rspResult.message) || error.message)) || '图片上传失败，请重新上传';
        this.$message.error(message);
        throw error;
      } finally {
        this.uploadLoading = false;
      }
      if (image) await this.processNewImage(image);
    },
    async processNewImage(image) {
      if (this.disabled) return;
      const imageKey = this.getImageKey(image);
      if (this.processingImageIds.includes(imageKey)) return;
      this.processLoading = true;
      this.processingImageIds.push(imageKey);
      try {
        if (this.mockEnabled) {
          this.$emit('replace', preferProcessedImages(processMockImages(this.part, [image], this.siteType)));
          return;
        }
        const res = await refinePart({
          operation: AI_OPERATION.PROCESS_NEW_IMAGES,
          siteType: this.siteType,
          regenerateImages: true,
          part: this.part,
          newImages: [{
            clientImageId: image.clientImageId,
            hashCode: image.originalHashCode,
            originalName: image.originalName
          }]
        });
        const data = extractRspData(res);
        const nextPart = data.part || ((data.parts || [])[0]);
        if (nextPart) {
          this.$emit('replace', preferProcessedImages(normalizePart(nextPart, this.siteType)));
          return;
        }
        const processed = (data.images || data.productPictureList || []).map(normalizeImageItem);
        processed.forEach(nextImage => {
          const index = this.part.productPictureList.findIndex(item => item.clientImageId === nextImage.clientImageId || item.originalHashCode === nextImage.originalHashCode);
          if (index > -1) {
            const currentImage = this.part.productPictureList[index];
            nextImage = {
              ...currentImage,
              ...nextImage,
              previewUrl: currentImage.previewUrl || nextImage.previewUrl,
              originalPreviewUrl: currentImage.originalPreviewUrl || nextImage.originalPreviewUrl || currentImage.previewUrl
            };
            if (nextImage.processedHashCode && nextImage.processStatus !== 'failed') {
              nextImage.selectedImageType = IMAGE_TYPE.PROCESSED;
              nextImage.selectedHashCode = nextImage.processedHashCode;
            }
            this.part.productPictureList.splice(index, 1, nextImage);
          }
        });
        syncMainFlag(this.part);
        this.$emit('change', this.part);
      } catch (error) {
        image.processStatus = 'failed';
        image.processMessage = 'AI优化失败，已保留原图';
        image.selectedImageType = IMAGE_TYPE.ORIGINAL;
        image.selectedHashCode = image.originalHashCode;
        syncMainFlag(this.part);
        this.$emit('change', this.part);
      } finally {
        this.processingImageIds = this.processingImageIds.filter(item => item !== imageKey);
        this.processLoading = false;
      }
    },
    async selectVersion(image, type) {
      if (this.disabled) return;
      if (type === IMAGE_TYPE.PROCESSED && !image.processedHashCode) {
        await this.processNewImage(image);
        return;
      }
      selectImageVersion(this.part, image, type);
      this.$emit('change', this.part);
    },
    setMain(image) {
      if (this.disabled) return;
      setPartMainImage(this.part, image);
      this.$emit('change', this.part);
    },
    removeImage(image) {
      if (this.disabled) return;
      this.part.productPictureList = this.part.productPictureList.filter(item => item.clientImageId !== image.clientImageId);
      if (this.part.defaultPhoto === image.selectedHashCode) {
        this.part.defaultPhoto = this.part.productPictureList[0] ? this.part.productPictureList[0].selectedHashCode : '';
      }
      syncMainFlag(this.part);
      this.$emit('change', this.part);
    }
  }
};
</script>

<style lang="scss" scoped>
.product-image-manager {
  margin-top: 18px;
}
.product-image-manager.is-disabled {
  opacity: .72;
}
.image-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}
.image-card,
.add-card {
  position: relative;
  width: 108px;
  min-height: 128px;
}
.image-preview-box {
  position: relative;
  width: 108px;
  height: 108px;
  overflow: hidden;
  border-radius: 8px;
}
.image-card img {
  width: 108px;
  height: 108px;
  object-fit: contain;
  background: #fff;
  border: 1px solid #cddbf0;
  border-radius: 8px;
  box-sizing: border-box;
  display: block;
}
.image-card.is-main img {
  border-color: #4875ff;
  box-shadow: 0 0 0 3px rgba(72, 117, 255, .18);
}
.image-hover-actions {
  position: absolute;
  z-index: 3;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border-radius: 8px;
  background: rgba(7, 31, 67, .46);
  color: #fff;
  opacity: 0;
  transition: opacity .16s ease;
}
.image-preview-box::after {
  content: none;
}
.image-preview-box:hover .image-hover-actions {
  opacity: 1;
}
.image-hover-actions button {
  border: 0;
  padding: 0;
  background: transparent;
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
}
.image-hover-actions button:disabled {
  color: rgba(255, 255, 255, .6);
  cursor: not-allowed;
}
.image-hover-actions i {
  width: 1px;
  height: 12px;
  background: rgba(255, 255, 255, .7);
}
.remove-img {
  position: absolute;
  z-index: 2;
  right: -7px;
  top: -7px;
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 50%;
  background: #5f6d82;
  color: #fff;
  font-size: 16px;
  cursor: pointer;
}
.main-badge {
  position: absolute;
  z-index: 2;
  left: 8px;
  top: 8px;
  padding: 2px 7px;
  border-radius: 999px;
  background: rgba(255, 255, 255, .86);
  color: #5b6d88;
  font-size: 12px;
  font-weight: 800;
}
.version-toggle {
  display: inline-flex;
  margin-top: 7px;
  border: 1px solid #d8e3f2;
  border-radius: 999px;
  overflow: hidden;
  background: #fff;
}
.version-toggle button {
  height: 24px;
  min-width: 48px;
  padding: 0 8px;
  border: 0;
  background: transparent;
  color: #7c8ba2;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}
.version-toggle button.is-active {
  color: #1a5cff;
  background: #eef5ff;
}
.version-toggle button:disabled {
  color: #c4ccd9;
  cursor: not-allowed;
}
.process-tip {
  margin: 6px 0 0;
  color: #c46a13;
  font-size: 12px;
  line-height: 1.35;
}
.image-add-upload,
::v-deep .image-add-upload .el-upload {
  width: 108px;
  height: 108px;
}
.add-card {
  width: 108px;
  height: 108px;
  min-height: 108px;
  box-sizing: border-box;
  display: grid;
  place-items: center;
  border: 1px dashed #cbdaf0;
  border-radius: 8px;
  color: #657187;
  font-size: 34px;
  background: #fff;
  cursor: pointer;
}
.image-requirement {
  margin: 12px 0 0;
  color: #8390a8;
  font-size: 13px;
}
::v-deep .product-image-preview-dialog .el-dialog {
  border-radius: 10px;
}
::v-deep .product-image-preview-dialog .el-dialog__header {
  padding: 12px 16px 0;
}
::v-deep .product-image-preview-dialog .el-dialog__body {
  padding: 18px 22px 24px;
}
.large-preview-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 420px;
  border-radius: 8px;
  background: transparent;
}
.large-preview-wrap img {
  max-width: 100%;
  max-height: 68vh;
  object-fit: contain;
}

.remove-img:disabled,
.image-hover-actions button:disabled {
  cursor: not-allowed;
}
</style>
